import { z } from "zod";
export type {
  NumberPurchasePrice,
  NumberPurchasePolicy,
  NumberPurchaseQuote,
  NumberPurchaseOperation,
  NumberPurchaseSearch,
} from "./number-purchases";
export type { SetupCheck, SetupProgress, SetupTab } from "./onboarding";
export {
  serviceIds,
  serviceSnapshotSchema,
  serviceStatusSchema,
  type ServiceSnapshot,
  type ServiceStatus,
} from "./service-status";
export type {
  CarrierPage,
  CarrierNumber,
  CarrierConnection,
  CarrierOffer,
  CarrierStatus,
  TwilioNumber,
  TwilioNumberPage,
} from "./carriers";
import {
  accountKinds,
  accountStatuses,
  orderStatuses,
  roles,
  type ActivationCommand,
  type OperatorIdentity,
  type ProblemDetails,
} from "@voice/domain";

export const accountSchema = z.object({
  id: z.string().uuid(),
  providerId: z.string().uuid(),
  accountNumber: z.string(),
  kind: z.enum(accountKinds),
  status: z.enum(accountStatuses),
  name: z.string(),
  contactName: z.string(),
  billingEmail: z.string().email(),
  planName: z.string().nullable(),
  primaryNumber: z.string().nullable(),
  monthlyRecurringMinor: z.number().int(),
  version: z.number().int(),
  createdAt: z.string(),
});
export type Account = z.infer<typeof accountSchema>;

export const planSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(accountKinds),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  recurringMinor: z.number().int(),
  unit: z.enum(["month", "seat"]),
  features: z.array(z.string()),
});
export type Plan = z.infer<typeof planSchema>;

export const serviceOrderSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.string(),
  accountId: z.string().uuid(),
  accountName: z.string(),
  type: z.string(),
  status: z.enum(orderStatuses),
  currentStep: z.string().nullable(),
  failureCode: z.string().nullable(),
  failureMessage: z.string().nullable(),
  updatedAt: z.string(),
  completedSteps: z.array(z.string()).default([]),
  attempts: z.record(z.number()).default({}),
});
export type ServiceOrder = z.infer<typeof serviceOrderSchema>;

export type AccountWorkspace = {
  account: Account;
  locations: Array<{
    id: string;
    name: string;
    formattedAddress: string;
    e911Status: "unvalidated" | "pending" | "validated" | "rejected";
  }>;
  subscriptions: Array<{
    id: string;
    planName: string;
    quantity: number;
    status: string;
    recurringMinor: number;
  }>;
  users: Array<{
    id: string;
    displayName: string;
    extension: string | null;
    roleLabel: string;
    lineId: string | null;
  }>;
  lines: Array<{
    id: string;
    label: string;
    extension: string | null;
    status: string;
  }>;
  numbers: Array<{
    id: string;
    e164: string;
    display: string;
    status: string;
    routeLabel: string;
    e911Status: string;
  }>;
  devices: Array<{
    id: string;
    model: string;
    macAddress: string;
    status: string;
    observedStatus: string;
    lineLabel: string | null;
    lastSeenAt: string | null;
  }>;
  routes: Array<{
    id: string;
    label: string;
    version: number;
    summary: string;
  }>;
  billing: {
    recurringMinor: number;
    estimatedTaxMinor: number;
    estimatedTotalMinor: number;
  };
  orders: ServiceOrder[];
  auditEvents: Array<{
    id: string;
    action: string;
    summary: string;
    actorName: string;
    occurredAt: string;
  }>;
  health: {
    status: "healthy" | "degraded" | "pending" | "drifted";
    desiredVersion: number;
    observedVersion: number;
    path: Array<{
      component: string;
      label: string;
      status: string;
      latencyMs: number | null;
    }>;
  };
};

export type CursorPage<T> = { items: T[]; nextCursor: string | null };

export class ApiError extends Error {
  constructor(readonly problem: ProblemDetails) {
    super(problem.detail);
  }
}

export class VoiceApiClient {
  constructor(
    private readonly baseUrl = "",
    private readonly getToken: () => string = () => "dev-platform-admin",
  ) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.getToken()}`,
        ...init.headers,
      },
    });
    if (!response.ok)
      throw new ApiError((await response.json()) as ProblemDetails);
    return (await response.json()) as T;
  }

  me(): Promise<OperatorIdentity> {
    return this.request("/v1/me");
  }

  listAccounts(
    params: {
      query?: string;
      kind?: string;
      status?: string;
      cursor?: string;
    } = {},
  ) {
    const query = new URLSearchParams(
      Object.entries(params).filter((entry): entry is [string, string] =>
        Boolean(entry[1]),
      ),
    );
    return this.request<CursorPage<Account>>(`/v1/accounts?${query}`);
  }

  getWorkspace(accountId: string) {
    return this.request<AccountWorkspace>(
      `/v1/accounts/${accountId}/workspace`,
    );
  }

  listPlans() {
    return this.request<CursorPage<Plan>>("/v1/plans");
  }

  listOrders() {
    return this.request<CursorPage<ServiceOrder>>("/v1/orders");
  }

  getOrder(orderId: string) {
    return this.request<ServiceOrder>(`/v1/orders/${orderId}`);
  }

  createAccount(
    input: {
      kind: "residential" | "commercial" | "enterprise";
      name: string;
      contactName: string;
      billingEmail: string;
    },
    idempotencyKey: string,
  ) {
    return this.request<Account>("/v1/accounts", {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(input),
    });
  }

  createLocation(accountId: string, input: unknown, idempotencyKey: string) {
    return this.request<{ id: string; e911Status: string }>(
      `/v1/accounts/${accountId}/locations`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify(input),
      },
    );
  }

  validateLocation(locationId: string, idempotencyKey: string) {
    return this.request<{ id: string; e911Status: string }>(
      `/v1/locations/${locationId}/validate`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
      },
    );
  }

  createUser(
    accountId: string,
    input: { displayName: string; extension: string; roleLabel: string },
    idempotencyKey: string,
  ) {
    return this.request<{ id: string; lineId: string }>(
      `/v1/accounts/${accountId}/users`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify(input),
      },
    );
  }

  createDevice(
    accountId: string,
    input: { model: string; macAddress: string },
    idempotencyKey: string,
  ) {
    return this.request<{ id: string }>(`/v1/accounts/${accountId}/devices`, {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(input),
    });
  }

  activateAccount(input: ActivationCommand, idempotencyKey: string) {
    return this.request<{ account: Account; order: ServiceOrder }>(
      `/v1/accounts/${input.accountId}/activate`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify(input),
      },
    );
  }

  correctPort(
    orderId: string,
    input: { authorizedName: string; serviceAddress: Record<string, string> },
    idempotencyKey: string,
  ) {
    return this.request<ServiceOrder>(
      `/v1/orders/${orderId}/correct-and-retry`,
      {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify(input),
      },
    );
  }
}

export const sessionRoleSchema = z.enum(roles);
export type {
  ServiceConnections,
  VoiceObservation,
} from "./service-connections";
export type {
  AccountDialing,
  DialingApplyOperation,
  DialingApplyResult,
} from "./local-dialing";
export {
  adminDashboardSchema,
  type AdminDashboardData,
} from "./admin-dashboard";
